import { Skeleton, Stack, Typography, useTheme, useMediaQuery, Theme, Box } from "@mui/material"
import { Plus } from "@untitled-ui/icons-react"
import numbro from "numbro"
import { Link } from "react-router-dom"

import usePoolDetails from "../../hooks/use-pool-details"
import { useTokenInfo } from "../../hooks/use-token-info"
import { PoolHistoricalPnlPool } from "../../types"
import { truncateId } from "../../utils/data-utils"
import { formatTicker } from "../../utils/format"
import { CopyToClipboard } from "../CopyToClipboard"
import FeeDisplay from "../FeeDisplay"
import { StatsWidget, StatsWidgetChange, StatsWidgetLabel, StatsWidgetValue } from "../StatsWidget"
import TokenLogo from "../TokenLogo"

import ActionButton from "../ui/ActionButton"

function PoolRow({ pool, search }: { pool: PoolHistoricalPnlPool; search: string }) {
  const { data: poolDetails, isLoading: isLoadingPoolDetails } = usePoolDetails(pool.amm_process)

  const [tokenA] = useTokenInfo(poolDetails?.tokenB)
  const [tokenB] = useTokenInfo(poolDetails?.tokenA)

  const theme = useTheme()
  const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"))

  if (!tokenA || !tokenB || !poolDetails) {
    return <Skeleton height="28px" />
  }

  if (search) {
    const searchLower = search.toLowerCase()
    if (
      !pool.amm_process?.toLowerCase().includes(searchLower) &&
      !tokenA.ticker?.toLowerCase().includes(searchLower) &&
      !tokenA.name?.toLowerCase().includes(searchLower) &&
      !tokenB.ticker?.toLowerCase().includes(searchLower) &&
      !tokenB.name?.toLowerCase().includes(searchLower)
    ) {
      return null
    }
  }

  console.log("pool", pool)

  const currentUserPNL = pool.pnl
  const last24HPnlChange =
    currentUserPNL - (pool.historical_pnl[pool.historical_pnl.length - 1]?.pnl_user || 0)

  const lastVolume = pool.volume24h || 0
  const volume24hAgo = pool.volume24hAgo || 0
  const last24HVolumeChange = lastVolume - volume24hAgo

  const currentApy = pool.historical_pnl[pool.historical_pnl.length - 1]?.pnl_percent || 0
  const last24hApy = pool.historical_pnl[pool.historical_pnl.length - 2]?.pnl_percent || 0
  const apyChange = currentApy - last24hApy

  if (isTablet) {
    return (
      <Stack
        sx={{
          backgroundColor: theme.palette.pools.enhanced,
          borderRadius: "12px",
        }}
      >
        <Box
          sx={(theme) => ({
            backgroundColor: theme.palette.pools.topBar,
            border: "1px solid",
            borderColor: theme.palette.pools.enhanced,
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
            paddingY: "1rem",
            paddingRight: 1,
            paddingLeft: 2.5,
          })}
        >
          <Stack gap={1.5} direction="row" alignItems="center" flexWrap={"wrap"}>
            <Stack
              gap={0.5}
              direction="row"
              alignItems="center"
              sx={{
                fill: "none",
                "&:hover": { fill: "var(--mui-palette-text-secondary)" },
              }}
            >
              <TokenLogo tx={tokenA?.logo} alt={tokenA?.ticker ?? ""} />
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1.25rem",
                }}
              >
                {formatTicker(tokenA?.ticker)}
              </Typography>
              <CopyToClipboard value={tokenA.id} />
            </Stack>
            <Stack
              gap={0.5}
              direction="row"
              alignItems="center"
              sx={{
                fill: "none",
                "&:hover": { fill: "var(--mui-palette-text-secondary)" },
              }}
            >
              <TokenLogo tx={tokenB?.logo} alt={tokenB?.ticker ?? ""} />
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1.25rem",
                }}
              >
                {formatTicker(tokenB?.ticker)}
              </Typography>
              <CopyToClipboard value={tokenB.id} />
            </Stack>
            <Typography
              sx={{
                fill: "var(--mui-palette-text-secondary)",
                paddingY: 0.25,
                paddingX: 0.5,
                borderRadius: 0.5,
                background: "var(--mui-palette-background-default)",
              }}
              variant="body1"
              color="text.secondary"
              component={"div"}
              fontWeight={700}
            >
              {truncateId(pool.amm_process)}
              <CopyToClipboard value={pool.amm_process} />
            </Typography>
            <FeeDisplay feebps={Number(poolDetails.fee)} />
          </Stack>
        </Box>
        <Stack
          gap={1}
          sx={(theme) => ({
            margin: 1,
            padding: 1,
            borderRadius: "12px",
            backgroundColor: theme.palette.pools.bg,

            "& > div": {
              flexGrow: 1,
              flexBasis: "max(45%, 180px)",
            },
          })}
          direction="row"
          flexWrap={"wrap"}
        >
          <StatsWidget>
            <StatsWidgetLabel>Your share</StatsWidgetLabel>
            <StatsWidgetValue color="text.primary">
              {numbro(pool.current_user_tvl).formatCurrency({
                average: true,
                mantissa: 2,
              })}
            </StatsWidgetValue>
          </StatsWidget>
          <StatsWidget>
            <StatsWidgetLabel>TVL</StatsWidgetLabel>
            <StatsWidgetValue color="text.primary">
              {numbro(pool.current_tvl).formatCurrency({
                average: true,
                mantissa: 2,
              })}
            </StatsWidgetValue>
          </StatsWidget>
          <StatsWidget>
            <StatsWidgetLabel>Total PNL from Pool</StatsWidgetLabel>
            <StatsWidgetValue
              color={currentUserPNL >= 0 ? theme.palette.success.main : theme.palette.error.main}
            >
              {numbro(currentUserPNL).formatCurrency({
                average: true,
                mantissa: 2,
              })}
            </StatsWidgetValue>
            <StatsWidgetChange
              delta={last24HPnlChange > 0 ? 1 : -1}
              value={numbro(last24HPnlChange).format({
                thousandSeparated: true,
                mantissa: 2,
                average: true,
              })}
            >
              in last 24 hrs
            </StatsWidgetChange>
          </StatsWidget>
          <StatsWidget>
            <StatsWidgetLabel>Total Volume</StatsWidgetLabel>
            <StatsWidgetValue color="text.primary">
              {numbro(pool.total_volume).formatCurrency({
                average: true,
                mantissa: 2,
              })}
            </StatsWidgetValue>
          </StatsWidget>
          <StatsWidget>
            <StatsWidgetLabel>24hr Volume</StatsWidgetLabel>
            <StatsWidgetValue color={theme.palette.accent.main}>
              {numbro(lastVolume).formatCurrency({
                average: true,
                mantissa: 2,
              })}
            </StatsWidgetValue>
            <StatsWidgetChange
              delta={last24HVolumeChange < 0 ? -1 : 1}
              value={numbro(last24HVolumeChange).format({
                thousandSeparated: true,
                mantissa: 2,
                average: true,
              })}
            >
              in last 24 hrs
            </StatsWidgetChange>
          </StatsWidget>
          <StatsWidget>
            <StatsWidgetLabel>30D PnL</StatsWidgetLabel>
            <StatsWidgetValue color="text.primary">
              {numbro(pool.pnl_30d_ago).formatCurrency({
                average: true,
                mantissa: 2,
              })}
            </StatsWidgetValue>
          </StatsWidget>
          <StatsWidget>
            <StatsWidgetLabel>30D PnL %</StatsWidgetLabel>
            <StatsWidgetValue
              color={pool.total_apy >= 0 ? theme.palette.success.main : theme.palette.error.main}
            >
              {numbro(pool.total_apy).format({ output: "percent", mantissa: 2 })}
            </StatsWidgetValue>
            <StatsWidgetChange
              delta={apyChange >= 0 ? 1 : -1}
              value={numbro(apyChange).format({
                output: "percent",
                mantissa: 2,
              })}
            >
              in last 24 hrs
            </StatsWidgetChange>
          </StatsWidget>
          <StatsWidget>
            <StatsWidgetLabel>Your Pool Share</StatsWidgetLabel>
            <StatsWidgetValue color="text.primary">
              {numbro(pool.user_share).format({
                average: true,
                mantissa: 2,
                output: "percent",
              })}
            </StatsWidgetValue>
          </StatsWidget>
          <Stack gap={1} direction="row" alignItems="center">
            <Link
              to={`/add?tokenA=${poolDetails.tokenA}&tokenB=${poolDetails.tokenB}&fee=${poolDetails.fee}`}
              style={{
                color: "var(--mui-palette-accent-main)",
                fontSize: "0.75rem",
                fontWeight: 700,
                textDecoration: "none",
              }}
            >
              <ActionButton icon={<Plus style={{ height: 18, width: 18 }} />} variant="accent">
                Add
              </ActionButton>
            </Link>
            <Link
              to={`/remove?pool=${pool.amm_process}`}
              style={{
                fontSize: "0.75rem",
                fontWeight: 700,
                textDecoration: "none",
                color: "var(--mui-palette-accent-main)",
              }}
            >
              <ActionButton>Withdraw</ActionButton>
            </Link>
          </Stack>
        </Stack>
      </Stack>
    )
  }

  return (
    <Stack direction="column" spacing={2}>
      <Stack direction="column">
        <Stack
          direction="row"
          justifyContent={"space-between"}
          sx={(theme) => ({
            backgroundColor: theme.palette.pools.topBar,
            border: "1px solid",
            borderColor: theme.palette.pools.enhanced,
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
            paddingY: "1rem",
            paddingRight: 1,
            paddingLeft: 2.5,
          })}
        >
          <Stack gap={1.5} direction="row" alignItems="center">
            <Stack
              gap={0.5}
              direction="row"
              alignItems="center"
              sx={{
                fill: "none",
                "&:hover": { fill: "var(--mui-palette-text-secondary)" },
              }}
            >
              <TokenLogo tx={tokenA?.logo} alt={tokenA?.ticker ?? ""} />
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1.25rem",
                }}
              >
                {formatTicker(tokenA?.ticker)}
              </Typography>
              <CopyToClipboard value={tokenA.id} />
            </Stack>
            <Stack
              gap={0.5}
              direction="row"
              alignItems="center"
              sx={{
                fill: "none",
                "&:hover": { fill: "var(--mui-palette-text-secondary)" },
              }}
            >
              <TokenLogo tx={tokenB?.logo} alt={tokenB?.ticker ?? ""} />
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1.25rem",
                }}
              >
                {formatTicker(tokenB?.ticker)}
              </Typography>
              <CopyToClipboard value={tokenB.id} />
            </Stack>
            <Typography
              sx={{
                fill: "var(--mui-palette-text-secondary)",
                paddingY: 0.25,
                paddingX: 0.5,
                borderRadius: 0.5,
                background: "var(--mui-palette-background-default)",
              }}
              variant="body1"
              color="text.secondary"
              component={"div"}
              fontWeight={700}
            >
              {truncateId(pool.amm_process)}
              <CopyToClipboard value={pool.amm_process} />
            </Typography>
            <FeeDisplay feebps={Number(poolDetails.fee)} />
          </Stack>
          <Stack gap={1} direction="row" alignItems="center">
            <Link
              to={`/add?tokenA=${poolDetails.tokenA}&tokenB=${poolDetails.tokenB}&fee=${poolDetails.fee}`}
              style={{
                color: "var(--mui-palette-accent-main)",
                fontSize: "0.75rem",
                fontWeight: 700,
                textDecoration: "none",
              }}
            >
              <ActionButton icon={<Plus style={{ height: 18, width: 18 }} />} variant="accent">
                Add
              </ActionButton>
            </Link>
            <Link
              to={`/remove?pool=${pool.amm_process}`}
              style={{
                fontSize: "0.75rem",
                fontWeight: 700,
                textDecoration: "none",
                color: "var(--mui-palette-accent-main)",
              }}
            >
              <ActionButton>Withdraw</ActionButton>
            </Link>
          </Stack>
        </Stack>
        <Stack
          sx={(theme) => ({
            backgroundColor: theme.palette.pools.enhanced,
            padding: 1,
            borderBottomLeftRadius: "12px",
            borderBottomRightRadius: "12px",
          })}
          direction="row"
        >
          <Stack
            direction="row"
            spacing={4}
            sx={{
              backgroundColor: theme.palette.pools.bg,
              padding: "1.5rem 1rem",
              borderTopLeftRadius: "0.75rem",
              borderBottomLeftRadius: "0.75rem",
              borderRight: `1px solid ${theme.palette.pools.enhanced}`,
            }}
          >
            <StatsWidget>
              <StatsWidgetLabel>Your Share</StatsWidgetLabel>
              <StatsWidgetValue color="text.primary">
                {numbro(pool.current_user_tvl).formatCurrency({
                  average: true,
                  mantissa: 2,
                })}
              </StatsWidgetValue>
              <StatsWidgetLabel>
                <Typography color="text.secondary">
                  TVL{" "}
                  {numbro(pool.current_tvl).formatCurrency({
                    average: true,
                    mantissa: 2,
                  })}
                </Typography>
              </StatsWidgetLabel>
            </StatsWidget>
            <StatsWidget>
              <StatsWidgetLabel>Total PNL from Pool</StatsWidgetLabel>
              <StatsWidgetValue
                color={currentUserPNL >= 0 ? theme.palette.success.main : theme.palette.error.main}
              >
                {numbro(currentUserPNL).formatCurrency({
                  average: true,
                  mantissa: 2,
                })}
              </StatsWidgetValue>
              <StatsWidgetChange
                delta={last24HPnlChange > 0 ? 1 : -1}
                value={numbro(last24HPnlChange).format({
                  thousandSeparated: true,
                  mantissa: 2,
                  average: true,
                })}
              >
                in last 24 hrs
              </StatsWidgetChange>
            </StatsWidget>
          </Stack>
          <Stack
            direction="row"
            spacing={4}
            sx={{
              backgroundColor: theme.palette.pools.bg,
              padding: "1.5rem 1rem",
              borderLeft: `1px solid ${theme.palette.pools.enhanced}`,
              borderRight: `1px solid ${theme.palette.pools.enhanced}`,
            }}
          >
            <StatsWidget>
              <StatsWidgetLabel>Total Volume</StatsWidgetLabel>
              <StatsWidgetValue color="text.primary">
                {numbro(pool.total_volume).formatCurrency({
                  average: true,
                  mantissa: 2,
                })}
              </StatsWidgetValue>
            </StatsWidget>
            <StatsWidget>
              <StatsWidgetLabel>24hr Volume</StatsWidgetLabel>
              <StatsWidgetValue color={theme.palette.accent.main}>
                {numbro(lastVolume).formatCurrency({
                  average: true,
                  mantissa: 2,
                })}
              </StatsWidgetValue>
              <StatsWidgetChange
                delta={last24HVolumeChange < 0 ? -1 : 1}
                value={numbro(last24HVolumeChange).format({
                  thousandSeparated: true,
                  mantissa: 2,
                  average: true,
                })}
              >
                in last 24 hrs
              </StatsWidgetChange>
            </StatsWidget>
          </Stack>
          <Stack
            direction="row"
            spacing={4}
            sx={{
              backgroundColor: theme.palette.pools.bg,
              padding: "1.5rem 1rem",
              borderLeft: `1px solid ${theme.palette.pools.enhanced}`,
              borderRight: `1px solid ${theme.palette.pools.enhanced}`,
            }}
          >
            <StatsWidget>
              <StatsWidgetLabel>30D PnL</StatsWidgetLabel>
              <StatsWidgetValue color="text.primary">
                {numbro(pool.pnl_30d_ago).formatCurrency({
                  average: true,
                  mantissa: 2,
                })}
              </StatsWidgetValue>
            </StatsWidget>
            <StatsWidget>
              <StatsWidgetLabel>30D PnL %</StatsWidgetLabel>
              <StatsWidgetValue
                color={pool.total_apy >= 0 ? theme.palette.success.main : theme.palette.error.main}
              >
                {numbro(pool.total_apy).format({ output: "percent", mantissa: 2 })}
              </StatsWidgetValue>
              <StatsWidgetChange
                delta={apyChange >= 0 ? 1 : -1}
                value={numbro(apyChange).format({
                  output: "percent",
                  mantissa: 2,
                })}
              >
                in last 24 hrs
              </StatsWidgetChange>
            </StatsWidget>
          </Stack>
          <Stack
            direction="row"
            spacing={4}
            sx={{
              backgroundColor: theme.palette.pools.bg,
              padding: "1.5rem 1rem",
              borderTopRightRadius: "0.75rem",
              borderBottomRightRadius: "0.75rem",
              borderLeft: `1px solid ${theme.palette.pools.enhanced}`,
              flexGrow: 1,
              justifyContent: "flex-end",
            }}
          >
            <StatsWidget>
              <StatsWidgetLabel>Your Pool Share</StatsWidgetLabel>
              <StatsWidgetValue color="text.primary" align="right">
                {numbro(pool.user_share).format({
                  average: true,
                  mantissa: 2,
                  output: "percent",
                })}
              </StatsWidgetValue>
            </StatsWidget>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  )
}

export default function PoolsStack({
  search,
  pools,
}: {
  search: string
  pools: PoolHistoricalPnlPool[]
}) {
  return (
    <Stack direction="column" spacing={4}>
      {pools.map((pool: PoolHistoricalPnlPool) => (
        <PoolRow key={pool.amm_process} pool={pool} search={search} />
      ))}
    </Stack>
  )
}
